import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/research-query.scss";
import {
  Button,
  Box,
  IconButton,
  Grid,
  Autocomplete,
  TextField,
  ListItemText,
  Tooltip,
  MenuItem,
  Slider,
  Typography,
  Tabs,
  Tab,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";
import useFetchActiveAuthTagsList from "../../hooks/useFetchActiveAuthTagsList";
import Loader from "../../components/Molecules/Loader/Loader";
import { createSetLoading, useToast } from "../../services/utils";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GenericQueriesType,
  validationScoringConfigDefaults,
  scoringSeverityOptions,
} from "../../services/constants";
import { replaceQueryFromDomainMapped } from "../../services/utils";
import ReadMoreLess from "../../components/ReadMoreLess";
import useFetchLinkedServices from "../../hooks/useFetchLinkedServices";
import useOptimizedConnectionKeys from "../../utils/useOptimizedConnectionKeys";
import useFetchResourcesByIdMultipleIds from "../../hooks/useFetchResourcesByMultipleIds";
import useFetchAllResources from "../../hooks/useFetchAllResources";
import { useAuth } from "../../contexts/AuthContext";
import useFetchResourceColumnsByMultipleIds from "../../hooks/useFetchResourceColumnsByMultipleIds";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { getAllConnectionKeys } from "../../services/connectionKeysService";
import GenericResourceQueryTab from "./GenericResourceQueryTab";
import GenericExternalQueryTab from "./GenericExternalQueryTab";
import GenericResearchQueriesEmptyState from "../../components/ResearchQuery/GenericResearchQueriesEmptyState";
import MixedQueryEditorDialog from "./MixedQueryV2/MixedQueryEditorDialog";
import useMixedQueryV2State from "./MixedQueryV2/useMixedQueryV2State";
import {
  MAX_SUBQUERIES,
  buildMixedSource,
  getSubQueryLabel,
  hydrateResourceColumns,
  hydrateResourceColumnsForResource,
  mapSourceToSubQueries,
  resolveMixedQueryReadOnly,
} from "./MixedQueryV2/mixedQueryV2.utils";
import { getResourceColumnsByMultipleIds } from "../../services/resourcesService";
import "../../styles/_mixedQueryV2.scss";
import {
  IResearchQuery,
  IResearchQueryDetail,
} from "../../types/researchQuery";
import { GridColDef } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { useNavigate, useParams } from "react-router-dom";
import DataTable from "../../components/DataGrids/DataGrid";
import {
  GenericResourceFormSchema,
  genericResearchQueryScoringSchema,
} from "../../schemas";
import {
  collectSchemaErrors,
  getImpactSumError,
} from "../../utils/scoringConfigFormValidation";
import useFetchAllRules from "../../hooks/useFetchAllRules";
import {
  addGenericResearchQuery,
  getGenericResearchQueryDetail,
  updateGenericResearchQuery,
  cloneGenericResearchQuery,
} from "../../services/ResearchQueriesService";
import {
  createGenericRQScoringConfig,
  getGenericRQScoringConfigByQuery,
  updateGenericRQScoringConfig,
} from "../../services/scoringService";
import useFetchGenericResearchQueryById from "../../hooks/useFetchGenericResearchQueryById";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";
import { EditAuthTags, ViewAuthTags } from "../../components/AuthTags";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconBtnEditBase,
} from "../../common/utils/icons";
import CloneResearchQueryDialog from "../../components/Dialogs/CloneResearchQueryDialog";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { SLIDER_ACCENT } from "../../components/Scoring/scoreTheme";
interface AllRules {
  id: number;
  name: string;
}
interface GenericResearchQueryProps {
  researchQuery?: any;
  setResearchQuery?: any;
  isFromExecution?: boolean;
  isEditResearchQuery?: boolean;
  setIsEditResearchQuery?: any;
  isViewOnly?: boolean;
  allowExecutionOverride?: boolean;
}

export default function GenericResearchQuery({
  researchQuery,
  setResearchQuery,
  isFromExecution = false,
  isEditResearchQuery = false,
  setIsEditResearchQuery,
  isViewOnly = false,
  allowExecutionOverride = false,
}: GenericResearchQueryProps) {
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useAuth();

  // Permission checks for generic research query actions
  const canEditGenericResearchQuery = hasPermission(
    "generic_research_query",
    "edit",
  );
  const canAddGenericResearchQuery = hasPermission(
    "generic_research_query",
    "add",
  );
  const canExecuteGenericResearchQuery = hasPermission(
    "generic_research_query",
    "execute",
  );
  const [columns, setColumns] = useState<any>({
    resourceColumns: [],
    missingColumns: [],
    mismatchedColumns: [],

    mixedQueryData: [
      {
        name: "",
        columns: [],
      },
    ],
  });

  const {
    allResourcesData,
    setAllResourcesData,
    resourceData,
    setResourceData,
    resourceColIds,
    setResourceColIds,
    resourceColumnsData,
    setResourceColumnsData,
    viewInlineVariables,
    globalVariables,
    setGlobalVariables,
    linkedServicesData,
    setLinkedServicesData,
    fetchedConnectionKeys,
    setFetchedConnectionKeys,
    setQueryBuilderTempValue,
    setTemporaryQueryValue,
    resourceIds,
    setShowAggregatedColumns,
  } = useRuleResourceContext();
  const { id: currentRQId } = useParams();
  const [currentResearchQueryId, setCurrentResearchQueryId] = useState<
    number | null
  >(null);
  useEffect(() => {
    setCurrentResearchQueryId(Number(currentRQId));
  }, [currentRQId]);
  const [loadingStates, setLoadingStates] = useState({
    allRules: false,
    researchQueryById: false,
    connectionKey: false,
    linkedService: false,
    resourcesById: false,
    allResources: false,
    resourceColumnsByIds: false,
    authTags: false,
    cloneResearchQuery: false,
    editQuery: false,
  });
  const setLoading = createSetLoading(setLoadingStates);

  const { showToast } = useToast();
  const [allRulesData, setAllRulesData] = useState<AllRules[]>([]);
  const [allRules] = useFetchAllRules({ setIsLoading: setLoading("allRules") });
  useEffect(() => {
    setAllRulesData(allRules);
  }, [allRules]);
  // Use props if provided, otherwise use component state
  const [localResearchQueryDetailData, setLocalResearchQueryDetailData] =
    useState<any>({});

  const [localIsEditQuery, setLocalIsEditQuery] = useState<any>(false);
  const [formHasChanges, setFormHasChanges] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<string>("queries");
  const handleChangeTab = (_event: any, newValue: any) => {
    setActiveTab(newValue);
  };

  // Determine which data and setter to use based on props
  const researchQueryDetailData = researchQuery || localResearchQueryDetailData;
  const setResearchQueryDetailData =
    setResearchQuery || setLocalResearchQueryDetailData;

  const isEditQuery = isEditResearchQuery || localIsEditQuery;
  const setIsEditQuery = setIsEditResearchQuery || setLocalIsEditQuery;

  // Function to update the parent component's state
  const handleSetResearchQueryDetailData = (newData: any) => {
    if (setResearchQuery) {
      // If we have a setter from props, use it to update the parent
      setResearchQuery(newData);
    } else {
      // Otherwise, update our local state
      setResearchQueryDetailData(newData);
    }
  };

  // Only fetch data if we're not using props
  const [researchQueryDetail] = useFetchGenericResearchQueryById({
    setIsLoading: setLoading("researchQueryById"),
    currentRQId,
    isFromExecution,
  });

  useEffect(() => {
    // Only update local state if we're not using props
    if (researchQueryDetail && !researchQuery) {
      setLocalResearchQueryDetailData(researchQueryDetail);
    }
  }, [researchQueryDetail, researchQuery]);

  const [linkedServices] = useFetchLinkedServices({
    setIsLoading: setLoading("linkedService"),
  });
  const [expandedRow, setExpandedRow] = useState<any>([]);
  const [resourcesData] = useFetchResourcesByIdMultipleIds({
    resourceIds,
    setIsLoading: setLoading("resourcesById"),
  });
  const [allResources] = useFetchAllResources({
    setIsLoading: setLoading("allResources"),
  });
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds,
    setIsLoading: setLoading("resourceColumnsByIds"),
  });

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllAuthTagsData } = useAuthTagContext();

  // Fetch auth tags data directly in this component (following linkedServicesData pattern)
  const [authTagsData] = useFetchActiveAuthTagsList({
    setIsLoading: setLoading("authTags"),
  });

  // Update context state when auth tags data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (authTagsData) {
      setAllAuthTagsData(authTagsData);
    }
  }, [authTagsData, setAllAuthTagsData]);

  useEffect(() => {
    if (linkedServices) {
      setLinkedServicesData(linkedServices);
    }
  }, [linkedServices]);

  useEffect(() => {
    if (resourcesData) {
      setResourceData(resourcesData);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (allResources) {
      setAllResourcesData(allResources);
    }
  }, [allResources]);

  useEffect(() => {
    if (resourceColumns) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns]);

  // useEffect(() => {
  //   setCurrentBreadcrumbPage({
  //     name: ruleData?.name,
  //     id: ruleData?.id,
  //   });
  //   return () => {
  //     setCurrentBreadcrumbPage({ name: "", id: null });
  //   };
  // }, [ruleData]);

  const [queryType, setQueryType] = useState("");
  const [editFormData, setEditFormData] = useState();

  // Mixed Query V2 editor state (replaces the legacy GenericMixedQueryTab). The
  // shared sub-query editing state + field handlers live in useMixedQueryV2State;
  // the parent owns the query name and the main/report query.
  const [mixedQueryName, setMixedQueryName] = useState<string>("");
  const [mixedMainQuery, setMixedMainQuery] = useState<string>("");
  const {
    subQueries: mixedSubQueries,
    setSubQueries: setMixedSubQueries,
    activeIndex: mixedActiveIndex,
    setActiveIndex: setMixedActiveIndex,
    handleTypeChange: mixedHandleTypeChange,
    handleFieldChange: mixedHandleFieldChange,
    handleLinkedServiceChange: mixedHandleLinkedServiceChange,
    handleQueryChange: mixedHandleQueryChange,
    handleEditorFocus: mixedHandleEditorFocus,
    handleEditorBlur: mixedHandleEditorBlur,
    addSubQuery: mixedAddSubQuery,
    removeSubQuery: mixedRemoveSubQuery,
    handleToggleActive: mixedHandleToggleActive,
    getDependencyDisplayLabel: mixedGetDependencyDisplayLabel,
    handleSetExternalColumns: mixedHandleSetExternalColumns,
    mixedErrors,
    setMixedErrors,
    validateMixedQuery,
  } = useMixedQueryV2State({
    columns,
    setColumns,
    resourcesData: allResourcesData,
    maxSubQueries: MAX_SUBQUERIES,
    onFetchResourceColumns: (resourceId: number) => {
      hydrateResourceColumnsForResource(
        resourceId,
        allResourcesData,
        setColumns,
        getResourceColumnsByMultipleIds,
      ).catch(() => null);
    },
  });

  // const [checked, setChecked] = useState(true);
  const [isAddQueryBtnEnabled, setIsAddQueryBtnEnabled] = useState(true);

  // Comment before update dialog state
  const [openCommentConfirmation, setOpenCommentConfirmation] =
    useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [pendingUpdatedQueries, setPendingUpdatedQueries] = useState<
    IResearchQuery[] | undefined
  >(undefined);

  // Clone research query state
  const [cloneResearchQueryDialog, setCloneResearchQueryDialog] =
    useState<boolean>(false);
  const [cloneResearchQueryData, setCloneResearchQueryData] = useState<any>({
    name: "",
    code: "",
    description: "",
    generic_research_query_tags: [],
  });

  // Ref to always hold the latest researchQueryForm, preventing stale closure issues
  // in DataGrid's memoized renderCell callbacks (e.g., handleEditQuery).
  const researchQueryFormRef = useRef<IResearchQueryDetail | null>(null);
  // Prevents concurrent / double-click edit calls.
  const isEditQueryInProgressRef = useRef(false);
  // Mirrors fetchedConnectionKeys from context so renderCell closures always see the latest cache.
  const fetchedConnectionKeysRef = useRef<any[]>([]);

  const [researchQueryForm, setResearchQueryForm] =
    useState<IResearchQueryDetail>({
      name: "",
      code: "",
      description: "",
      rule_id: null,
      research_queries: [],
      generic_research_query_tags:
        researchQueryDetailData?.generic_research_query_tags ?? [],
      impact_of_record_level:
        researchQueryDetailData?.impact_of_record_level ??
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low: validationScoringConfigDefaults.factor_low,
      factor_medium: validationScoringConfigDefaults.factor_medium,
      factor_high: validationScoringConfigDefaults.factor_high,
    });
  const [initialResearchQueryForm, setInitialResearchQueryForm] =
    useState<IResearchQueryDetail>({
      name: "",
      code: "",
      description: "",
      rule_id: null,
      research_queries: [],
      generic_research_query_tags:
        researchQueryDetailData?.generic_research_query_tags ?? [],
      impact_of_record_level:
        researchQueryDetailData?.impact_of_record_level ??
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low: validationScoringConfigDefaults.factor_low,
      factor_medium: validationScoringConfigDefaults.factor_medium,
      factor_high: validationScoringConfigDefaults.factor_high,
    });
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [severityMappings, setSeverityMappings] = useState<
    Record<string, string>
  >({});
  const newMappingCounter = useRef(0);
  // Tracks the id of the persisted research-query-scoring-config row for the
  // current generic research query (null when no config has been created yet).
  const [scoringConfigId, setScoringConfigId] = useState<number | null>(null);

  // OPTIMIZATION: Use custom hook for optimized connection key fetching
  // This replaces the inefficient useFetchConnectionKeysAll call with targeted fetching
  const connectionKeys = useOptimizedConnectionKeys({
    dataSources: [researchQueryForm, researchQueryDetailData],
    setIsLoading: setLoading("connectionKey"),
    setFetchedConnectionKeys,
  });

  // Keep refs in sync on every render so handleEditQuery inside DataGrid's
  // memoized renderCell always reads the latest values without stale closures.
  researchQueryFormRef.current = researchQueryForm;
  fetchedConnectionKeysRef.current = fetchedConnectionKeys ?? [];

  const handleTransition = (_: boolean, actionType?: string) => {
    if (actionType === "add") {
      setIsEditQuery(false);
      setColumns((prev: any) => ({
        ...prev,
        resourceColumns: [],
      }));
    }
  };

  // Hydrate the Mixed V2 editor from editFormData once when a Mixed row opens.
  // Guarded so it does not re-run when columns/resources load asynchronously.
  const mixedHydratedIdRef = useRef<number | null>(null);
  useEffect(() => {
    const isMixedEdit =
      queryType === "Mixed" && isEditQuery && !!(editFormData as any);
    if (!isMixedEdit) {
      mixedHydratedIdRef.current = null;
      return;
    }
    const target: any = editFormData;
    if (mixedHydratedIdRef.current === target?.id) return;
    mixedHydratedIdRef.current = target?.id ?? null;
    const source = Array.isArray(target?.source)
      ? target.source
      : target?.source
        ? [target.source]
        : [];
    setMixedQueryName(target?.name || "");
    setMixedMainQuery(target?.query || "");
    setMixedSubQueries(mapSourceToSubQueries(source, linkedServicesData));
    setMixedActiveIndex(0);
    hydrateResourceColumns(
      source,
      allResourcesData,
      setColumns,
      getResourceColumnsByMultipleIds,
    ).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryType, isEditQuery, editFormData, linkedServicesData, allResourcesData]);

  // Close the Mixed V2 editor and reset the Mixed edit state. Clears the
  // validation errors so a previously-edited query's errors don't leak into the
  // next query opened for editing.
  const handleCloseMixedDialog = () => {
    setIsEditQuery(false);
    setQueryType("");
    setEditFormData(undefined);
    setIsAddQueryBtnEnabled(true);
    setMixedErrors({});
    mixedHydratedIdRef.current = null;
  };

  // Persist the Mixed query: rebuild the edited entry's source/name/query and
  // hand the full research_queries array to the shared save flow (which runs
  // validation + the comment-confirmation dialog in update mode).
  const handleSaveMixedQuery = () => {
    const current: any = editFormData;
    if (!current) return;
    // Field-level validation with parity to the legacy builder. Populates the
    // indexed mixedErrors object consumed by the V2 cards / Main Query editor.
    const isValid = validateMixedQuery(mixedMainQuery, mixedQueryName);
    if (!isValid) {
      showToast("Please fix validation errors", "error");
      return;
    }
    const existing = researchQueryForm?.research_queries ?? [];
    const updatedEntry = {
      id: current.id,
      name: mixedQueryName,
      query: mixedMainQuery,
      source: buildMixedSource(mixedSubQueries),
    };
    const idx = existing.findIndex((q: any) => q.id === current.id);
    const updatedQueries =
      idx !== -1
        ? existing.map((q: any, i: number) => (i === idx ? updatedEntry : q))
        : [...existing, updatedEntry];
    setResearchQueryForm((prev: any) => ({
      ...prev,
      research_queries: updatedQueries,
    }));
    handleSaveResearchQuery(updatedQueries as IResearchQuery[]);
    handleCloseMixedDialog();
  };

  useEffect(() => {
    if (researchQueryDetailData) {
      const updatedResearchQueryData =
        researchQueryDetailData?.research_queries?.map(
          (researchQuery: any, index: number) => {
            return {
              ...researchQuery,
              source: researchQuery?.source,
              id: index + 1,
              updatedQuery: researchQuery?.query,
              query: replaceQueryFromDomainMapped(
                researchQuery?.query,
                columns?.missingColumns ?? [],
              ),
            };
          },
        );
      setResearchQueryForm((prev: any) => ({
        ...prev,
        code: researchQueryDetailData?.code,
        name: researchQueryDetailData?.name,
        description: researchQueryDetailData?.description,
        research_queries: updatedResearchQueryData ?? [],
        generic_research_query_tags: Array.isArray(
          researchQueryDetailData?.generic_research_query_tags_details,
        )
          ? researchQueryDetailData.generic_research_query_tags_details.map(
              (tag: any) => tag.auth_tag_id,
            )
          : [],
        impact_of_record_level:
          researchQueryDetailData?.impact_of_record_level ??
          validationScoringConfigDefaults.impact_of_record_level,
        impact_of_issue_level:
          researchQueryDetailData?.impact_of_issue_level ??
          validationScoringConfigDefaults.impact_of_issue_level,
        factor_low:
          researchQueryDetailData?.factor_low ??
          validationScoringConfigDefaults.factor_low,
        factor_medium:
          researchQueryDetailData?.factor_medium ??
          validationScoringConfigDefaults.factor_medium,
        factor_high:
          researchQueryDetailData?.factor_high ??
          validationScoringConfigDefaults.factor_high,
        severity_mappings: researchQueryDetailData?.severity_mappings ?? {},
      }));
      setInitialResearchQueryForm((prev: any) => ({
        ...prev,
        code: researchQueryDetailData?.code,
        name: researchQueryDetailData?.name,
        description: researchQueryDetailData?.description,
        research_queries: updatedResearchQueryData ?? [],
        generic_research_query_tags: Array.isArray(
          researchQueryDetailData?.generic_research_query_tags_details,
        )
          ? researchQueryDetailData.generic_research_query_tags_details.map(
              (tag: any) => tag.auth_tag_id,
            )
          : [],
        impact_of_record_level:
          researchQueryDetailData?.impact_of_record_level ??
          validationScoringConfigDefaults.impact_of_record_level,
        impact_of_issue_level:
          researchQueryDetailData?.impact_of_issue_level ??
          validationScoringConfigDefaults.impact_of_issue_level,
        factor_low:
          researchQueryDetailData?.factor_low ??
          validationScoringConfigDefaults.factor_low,
        factor_medium:
          researchQueryDetailData?.factor_medium ??
          validationScoringConfigDefaults.factor_medium,
        factor_high:
          researchQueryDetailData?.factor_high ??
          validationScoringConfigDefaults.factor_high,
        severity_mappings: researchQueryDetailData?.severity_mappings ?? {},
      }));
      setSeverityMappings(researchQueryDetailData?.severity_mappings ?? {});
      if (!isFromExecution) {
        setGlobalVariables(researchQueryDetailData?.variables ?? {});
      }
      // Reset form changes state when loading initial data
      setFormHasChanges(false);
    }
  }, [researchQueryDetailData, columns?.missingColumns]);

  // Fetch the persisted research-query-scoring-config (separate entity) for
  // this generic research query and seed the scoring section + severity
  // mappings from it. Falls back to defaults when no config exists.
  useEffect(() => {
    if (!currentResearchQueryId) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getGenericRQScoringConfigByQuery(
          currentResearchQueryId,
        );
        if (cancelled) return;
        if (cfg && cfg.id) {
          setScoringConfigId(cfg.id);
          const mappings = cfg.severity_mappings ?? {};
          setSeverityMappings(mappings);
          setResearchQueryForm((prev: any) => ({
            ...prev,
            impact_of_record_level:
              cfg.impact_of_record_level ??
              validationScoringConfigDefaults.impact_of_record_level,
            impact_of_issue_level:
              cfg.impact_of_issue_level ??
              validationScoringConfigDefaults.impact_of_issue_level,
            factor_low:
              cfg.factor_low ?? validationScoringConfigDefaults.factor_low,
            factor_medium:
              cfg.factor_medium ??
              validationScoringConfigDefaults.factor_medium,
            factor_high:
              cfg.factor_high ?? validationScoringConfigDefaults.factor_high,
            severity_mappings: mappings,
          }));
          setInitialResearchQueryForm((prev: any) => ({
            ...prev,
            impact_of_record_level:
              cfg.impact_of_record_level ??
              validationScoringConfigDefaults.impact_of_record_level,
            impact_of_issue_level:
              cfg.impact_of_issue_level ??
              validationScoringConfigDefaults.impact_of_issue_level,
            factor_low:
              cfg.factor_low ?? validationScoringConfigDefaults.factor_low,
            factor_medium:
              cfg.factor_medium ??
              validationScoringConfigDefaults.factor_medium,
            factor_high:
              cfg.factor_high ?? validationScoringConfigDefaults.factor_high,
            severity_mappings: mappings,
          }));
        } else {
          setScoringConfigId(null);
        }
      } catch (err) {
        // 404 / missing config is expected when no scoring config has been
        // created for this query yet — fall through to defaults silently.
        if (!cancelled) setScoringConfigId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentResearchQueryId]);

  // Validate initialized/fetched scoring values too, not only user edits.
  useEffect(() => {
    let cancelled = false;
    collectSchemaErrors(
      genericResearchQueryScoringSchema,
      researchQueryForm,
    ).then((scoringErrors) => {
      if (cancelled) return;
      const scoringFields = [
        "impact_of_record_level",
        "impact_of_issue_level",
        "factor_low",
        "factor_medium",
        "factor_high",
      ];
      setErrors((previous) => {
        const next = { ...previous };
        scoringFields.forEach((field) => delete next[field]);
        return { ...next, ...scoringErrors };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [
    researchQueryForm.impact_of_record_level,
    researchQueryForm.impact_of_issue_level,
    researchQueryForm.factor_low,
    researchQueryForm.factor_medium,
    researchQueryForm.factor_high,
    // Intentionally validate only when scoring values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // useEffect(() => {
  //   if (ruleData?.rule_schema?.resources?.length > 0) {
  //     setResourceIds(ruleData.rule_schema.resources);
  //   }
  // }, [ruleData]);

  useEffect(() => {
    const formChanged =
      JSON.stringify(researchQueryForm?.name) !==
        JSON.stringify(initialResearchQueryForm?.name) ||
      JSON.stringify(researchQueryForm?.description) !==
        JSON.stringify(initialResearchQueryForm?.description) ||
      JSON.stringify(researchQueryForm?.generic_research_query_tags) !==
        JSON.stringify(initialResearchQueryForm?.generic_research_query_tags) ||
      JSON.stringify(researchQueryForm?.rule_id) !==
        JSON.stringify(initialResearchQueryForm?.rule_id) ||
      researchQueryForm?.impact_of_record_level !==
        initialResearchQueryForm?.impact_of_record_level ||
      researchQueryForm?.impact_of_issue_level !==
        initialResearchQueryForm?.impact_of_issue_level ||
      researchQueryForm?.factor_low !== initialResearchQueryForm?.factor_low ||
      researchQueryForm?.factor_medium !==
        initialResearchQueryForm?.factor_medium ||
      researchQueryForm?.factor_high !==
        initialResearchQueryForm?.factor_high ||
      JSON.stringify(researchQueryForm?.severity_mappings ?? {}) !==
        JSON.stringify(initialResearchQueryForm?.severity_mappings ?? {});
    setHasChanges(formChanged);
  }, [researchQueryForm, initialResearchQueryForm]);

  useEffect(() => {
    setResearchQueryForm((prev) => ({
      ...prev,
      severity_mappings: severityMappings,
    }));
  }, [severityMappings]);
  useEffect(() => {
    let resColIds: any[] = [];
    if (resourceData && resourceData.length > 0) {
      resourceData.map((res: any) => {
        resColIds.push(res.additional_properties?.resource_column_details_id);
      });
      setResourceColIds(resColIds);
    }
  }, [resourceData]);
  useEffect(() => {
    let missingColumns: any[] = [];
    let uniqueColumns: any[] = [];
    // let baseColumns: any[] =
    //   ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on.map(
    //     (column: any) => ({ name: `Key_${column}`, value: `Key_${column}` })
    //   );
    if (resourceColumnsData && Array.isArray(resourceColumnsData)) {
      resourceColumnsData.forEach((resCol: any) => {
        const matchedRes: any = resourceData.find(
          (res: any) =>
            res?.additional_properties?.resource_column_details_id ===
            resCol?.id,
        );
        // if (
        //   ruleData?.rule_schema?.merge_rule?.primary_dataset?.resource_id ===
        //   matchedRes?.id
        // ) {
        //   uniqueColumns =
        //     ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on;
        // } else {
        //   const matchingResource =
        //     ruleData?.rule_schema?.merge_rule?.secondary_datasets.find(
        //       (rule: any) => rule.resource_id === matchedRes?.id
        //     );
        //   if (matchingResource) {
        //     uniqueColumns = matchingResource.merge_on;
        //   }
        // }
        const missing = getMappedColumns(
          uniqueColumns,
          resCol?.resource_column_properties?.resource_columns,
          matchedRes ? matchedRes?.code : "",
        );

        missingColumns = [...missingColumns, ...missing];
      });
      // if (
      //   baseColumns &&
      //   baseColumns.length > 0 &&
      //   missingColumns &&
      //   missingColumns.length > 0
      // ) {
      //   const updatedBaseColumns = updateBaseColumns(
      //     baseColumns,
      //     missingColumns
      //   );
      //   missingColumns = updatedBaseColumns;
      // }
    } else {
      missingColumns = getMappedColumns(
        resourceColumnsData?.resource_column_properties?.unique_columns,
        resourceColumnsData?.resource_column_properties?.resource_columns,
        "",
      );
    }
    setColumns((prev: any) => ({
      ...prev,
      missingColumns: missingColumns,
      mismatchedColumns: missingColumns,
    }));
  }, [resourceColumnsData, resourceData]);

  // Commented out as it's not currently used
  /*
  const updateBaseColumns = (baseColumn: any[], missingColumns: any[]) => {
    // Function to extract the resource from the column name
    const getResource = (columnName: string) => {
      return columnName.split(".")[0];
    };

    // Function to normalize and extract the relevant part of the value
    const extractValue = (value: string) => {
      const parts = value.split(".");
      return parts.length > 1 ? parts[1] : parts[0];
    };

    // Group missingColumns by resource
    const resources = missingColumns.reduce((acc, item) => {
      const resource = getResource(item.name);
      if (!acc[resource]) acc[resource] = [];
      acc[resource].push(item);
      return acc;
    }, {});

    // Ensure each resource has the same number of objects
    const resourceKeys = Object.keys(resources);
    const numColumns = resources[resourceKeys[0]].length;

    const updatedBaseColumn = [...baseColumn];

    // Iterate over each index
    for (let i = 0; i < numColumns; i++) {
      const valuesAtIndex = resourceKeys.map((resource) => {
        return extractValue(resources[resource][i].value); // Extract the relevant part of the value
      });

      // Check if all values at this index match and are not null
      const allMatch = valuesAtIndex.every(
        (value) => value !== null && value === valuesAtIndex[0]
      );

      // Update baseColumn if all values match and none of them are null
      if (allMatch && valuesAtIndex[0] !== "null") {
        updatedBaseColumn[i].value = `Key_${valuesAtIndex[0]}`;
        updatedBaseColumn[i].name = `Key_${valuesAtIndex[0]}`;
      }
    }

    return updatedBaseColumn;
  };
  */

  const getMappedColumns = (
    columns: string[],
    allColumns: any[],
    prefix: string,
  ): { name: string; value: string }[] => {
    const result: { name: string; value: string }[] = [];
    if (columns) {
      for (const column of columns) {
        const matchedColumn =
          allColumns && allColumns.find((col) => col.column_name === column);

        if (matchedColumn) {
          result.push({
            name: `${prefix}.${matchedColumn.column_name}`,
            value:
              `${prefix}.${matchedColumn.domain_column}` ||
              `${prefix}.${matchedColumn.column_name}`,
          });
        }
      }
    }
    return result;
  };
  const handleSaveResearchQuery = async (updatedQueries?: IResearchQuery[]) => {
    try {
      if (isFromExecution) {
        // Create a new object with the updated research queries
        const updatedResearchQueryData = {
          ...researchQueryDetailData,
          research_queries: updatedQueries,
        };

        // Use the handler to update the parent's state
        handleSetResearchQueryDetailData(updatedResearchQueryData);
        return;
      }

      const hasEmptyOrTempKey = Object.keys(severityMappings).some(
        (k) => k === "" || k.startsWith("__new_"),
      );
      if (hasEmptyOrTempKey) {
        showToast(
          "Please select a query name for all severity mappings before saving.",
          "error",
        );
        return;
      }

      // Validate both schemas before any API request so scoring errors remain
      // visible even when the base research-query form is invalid too.
      const [baseErrors, scoringErrors] = await Promise.all([
        collectSchemaErrors(GenericResourceFormSchema, researchQueryForm),
        collectSchemaErrors(
          genericResearchQueryScoringSchema,
          researchQueryForm,
        ),
      ]);
      const validationErrors = { ...baseErrors, ...scoringErrors };
      if (Object.keys(validationErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...validationErrors }));
        return;
      }

      if (
        researchQueryDetailData &&
        Object.keys(researchQueryDetailData).length > 0
      ) {
        // Update mode: show comment dialog before saving
        setPendingUpdatedQueries(updatedQueries);
        setOpenCommentConfirmation(true);
      } else {
        // Create mode: save directly without comment dialog
        const cleanedSeverityMappings = Object.fromEntries(
          Object.entries(severityMappings).filter(
            ([k]) => k !== "" && !k.startsWith("__new_"),
          ),
        );
        const payload = {
          ...researchQueryForm,
          research_queries:
            updatedQueries ?? researchQueryForm?.research_queries,
          variables: globalVariables,
        };
        payload.severity_mappings = cleanedSeverityMappings;
        const { generic_research_query_tags, ...rest } = payload;
        const finalPayload = {
          generic_research_query: { ...rest },
          generic_research_query_tags,
        };
        addGenericResearchQuery(finalPayload)
          .then(async (response: any) => {
            if (response) {
              const result = await getGenericResearchQueryDetail(response?.id);
              setCurrentResearchQueryId(response?.id);
              setResearchQueryDetailData(result);
              // Persist the scoring config to its own entity so severity
              // mappings + factor/impact values survive (the GRQ payload
              // ignores these fields).
              await saveScoringConfigForQuery(
                response?.id,
                cleanedSeverityMappings,
              );
              showToast("Research Query created successfully!", "success");
              setFormHasChanges(false);
            }
          })
          .catch((error: any) => {
            //showToast(`Cannot create Research Query`, "error");
          });
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          },
        );
      }
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const handleCommentChange = (e: any) => {
    setComment(e.target.value);
  };

  // Persists the scoring section (impact / factor / severity_mappings) to the
  // research-query-scoring-config entity. Creates a new row when none exists
  // for this query, otherwise updates the existing one. The comment arg is
  // only used on the update path (PUT requires it).
  const saveScoringConfigForQuery = async (
    grqId: number,
    cleanMappings: Record<string, string>,
    commentText?: string,
  ) => {
    if (!grqId) return;
    const scoringPayload = {
      impact_of_record_level: Number(researchQueryForm?.impact_of_record_level),
      impact_of_issue_level: Number(researchQueryForm?.impact_of_issue_level),
      factor_low: Number(researchQueryForm?.factor_low),
      factor_medium: Number(researchQueryForm?.factor_medium),
      factor_high: Number(researchQueryForm?.factor_high),
      severity_mappings: cleanMappings,
    };
    try {
      // Re-check the server in case a config was created in another tab since
      // the page loaded — avoids accidentally creating a duplicate.
      let existingId = scoringConfigId;
      if (!existingId) {
        try {
          const existing = await getGenericRQScoringConfigByQuery(grqId);
          if (existing && existing.id) existingId = existing.id;
        } catch {
          // 404 is expected when none exists
        }
      }
      if (existingId) {
        await updateGenericRQScoringConfig(existingId, {
          ...scoringPayload,
          comment: commentText || "Updated from research query edit",
        });
      } else {
        const created = await createGenericRQScoringConfig({
          generic_research_query_id: grqId,
          ...scoringPayload,
        });
        if (created?.id) setScoringConfigId(created.id);
      }
    } catch (err) {
      console.error("Failed to save research query scoring config:", err);
      showToast("Failed to save scoring config / severity mappings", "error");
    }
  };

  const handleSaveComment = () => {
    setOpenCommentConfirmation(false);
    const queriesForSave =
      pendingUpdatedQueries ?? researchQueryForm?.research_queries;
    const cleanedSeverityMappings = Object.fromEntries(
      Object.entries(severityMappings).filter(
        ([k]) => k !== "" && !k.startsWith("__new_"),
      ),
    );
    const payload = {
      ...researchQueryForm,
      research_queries: queriesForSave,
      variables: globalVariables,
    };
    payload.severity_mappings = cleanedSeverityMappings;
    const { generic_research_query_tags, ...rest } = payload;
    const finalPayload = {
      generic_research_query: { ...rest, comment },
      generic_research_query_tags,
    };
    updateGenericResearchQuery({
      currentResearchQueryId,
      payload: finalPayload,
    })
      .then(async (response: any) => {
        if (response) {
          // Scoring config lives in a separate entity — persist it explicitly.
          if (currentResearchQueryId) {
            await saveScoringConfigForQuery(
              currentResearchQueryId,
              cleanedSeverityMappings,
              comment,
            );
          }
          showToast("Research Query updated successfully!", "success");
          setFormHasChanges(false);
        }
      })
      .catch((err: Error) => {
        console.error(err);
      });
    setComment("");
    setPendingUpdatedQueries(undefined);
  };

  const handleCancelComment = () => {
    setOpenCommentConfirmation(false);
    setComment("");
    setPendingUpdatedQueries(undefined);
  };

  const buildPayload = () => {
    const { generic_research_query_tags, ...rest } = researchQueryForm;
    const cleanedSeverityMappings = Object.fromEntries(
      Object.entries(severityMappings).filter(
        ([k]) => k !== "" && !k.startsWith("__new_"),
      ),
    );
    return {
      generic_research_query: {
        ...rest,
        research_queries: researchQueryForm?.research_queries,
        variables: globalVariables,
        severity_mappings: cleanedSeverityMappings,
      },
      generic_research_query_tags,
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const isEditMode = Boolean(currentResearchQueryId);
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/generic-research-queries/${currentResearchQueryId}`
          : "/generic-research-queries",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: currentResearchQueryId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const getLinkedServiceCodebyId = (id: number | string | null | undefined) => {
    const linkedData = linkedServicesData?.find((item: any) => item.id === id);
    return linkedData ? linkedData.name : null;
  };

  const handleExecuteQuery = () => {
    if (currentResearchQueryId) {
      navigate(
        `/research-query/research-query-execution/${currentResearchQueryId}`,
      );
    }
  };
  const getConnectionKeybyId = (id: number | string | null | undefined) => {
    // Use fetchedConnectionKeys from context (following RuleResearchQuery.tsx pattern)
    const connectionData = fetchedConnectionKeys?.find(
      (item: any) => item.id === id,
    );

    return connectionData ? connectionData?.name : null;
  };

  const getResearchColumns = (): GridColDef[] => {
    // Base columns that are always shown
    const baseColumns: GridColDef[] = [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: (params: any) => (
          <IconButton
            size="small"
            tabIndex={-1}
            disabled={params?.row?.source[0]?.type === "Mixed" ? false : true}
            onClick={() => {
              if (expandedRow.includes(params.row.id)) {
                setExpandedRow((prev: any) =>
                  prev.filter((item: any) => item !== params.row.id),
                );
              } else {
                setExpandedRow((prev: any) => [...prev, params.row.id]);
              }
            }}
          >
            {params?.row?.source[0]?.type === "Mixed" ? (
              <>
                <ExpandMoreIcon
                  sx={{
                    transform: `rotateZ(${
                      expandedRow.includes(params.row.id) ? 180 : 0
                    }deg)`,
                    transition: (theme) =>
                      theme.transitions.create("transform", {
                        duration: theme.transitions.duration.shortest,
                      }),
                  }}
                  fontSize="inherit"
                />
              </>
            ) : null}
          </IconButton>
        ),
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 50,
        renderCell: (params: any) => <span>{params.value}</span>,
      },
      {
        field: "name",
        headerName: "Query Name",
        minWidth: 250,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "query",
        headerName: "SQL Query",
        minWidth: 250,
        groupable: false,
        flex: 1,
        renderCell: (params: any) => {
          return (
            <>
              {params.value && params.value.length > 50 ? (
                <ReadMoreLess
                  data={params?.value?.replace(/"/g, "")}
                  charLimit={50}
                />
              ) : (
                params?.value?.replace(/"/g, "")
              )}
            </>
          );
        },
      },
    ];

    // Add columns based on execution mode
    if (!isFromExecution) {
      // Add Query Type column when not in execution mode
      baseColumns.splice(2, 0, {
        field: "source",
        headerName: "Query Type",
        minWidth: 140,
        renderCell: (params: any) => {
          return (
            <LimitChractersWithTooltip
              value={params?.value[0]?.type?.replace("_", " ")}
            />
          );
        },
      });

      // Add Linked Service column when not in execution mode
      baseColumns.push({
        field: "linked_service_id",
        headerName: "Connection Type",
        minWidth: 150,
        renderCell: (params: any) => {
          const linkedServiceCodebyId =
            getLinkedServiceCodebyId(
              params?.row?.source[0]?.linked_service_id,
            ) ?? "";

          return (
            <LimitChractersWithTooltip value={linkedServiceCodebyId ?? ""} />
          );
        },
      });

      // Add Connection Key column when not in execution mode
      baseColumns.push({
        field: "connection_key_id",
        headerName: "Connection Key",
        minWidth: 150,
        renderCell: (params: any) => {
          const connectionKeybyId =
            getConnectionKeybyId(params?.row?.source[0]?.connection_key_id) ??
            "";

          return <LimitChractersWithTooltip value={connectionKeybyId ?? ""} />;
        },
      });
    }

    // Add Action column with edit/delete buttons only when not in view-only mode
    if (isViewOnly) return baseColumns;

    // Only show delete button when not in execution mode
    baseColumns.push({
      field: "action",
      headerName: "Actions",
      width: 130,
      align: "center",
      headerAlign: "center",
      groupable: false,
      renderCell: (params: any) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const handleEditQuery = async () => {
          // Double-click / concurrent-call guard — drop the second invocation.
          if (isEditQueryInProgressRef.current) return;
          isEditQueryInProgressRef.current = true;
          setLoading("editQuery")(true);
          try {
            // Use the ref instead of the closed-over state to avoid stale closure
            // issues caused by DataGrid's internal memoization of renderCell.
            const formData: any =
              researchQueryFormRef.current?.research_queries.find(
                (query) => query.id === params.row.id,
              );

            let linkedService: any;
            let connectionKey: any;
            const isMixed = formData.source[0].type === "Mixed";
            const sources = isMixed ? formData.source : [formData.source[0]];

            // Find linked services (synchronous lookup — no API needed).
            linkedService = sources
              .map((src: any) =>
                linkedServicesData?.find(
                  (linked: any) => linked?.id === src?.linked_service_id,
                ),
              )
              .filter(Boolean);

            // Collect unique, non-null connection key IDs across all sources.
            const connectionKeyIds = sources
              .map((src: any) => src?.connection_key_id)
              .filter((id: any) => id !== null && id !== undefined);

            if (connectionKeyIds.length > 0) {
              // Check the ref-backed cache first — avoids an API round-trip when
              // the keys were already fetched by useOptimizedConnectionKeys.
              const cachedKeys = fetchedConnectionKeysRef.current;
              const missingKeyIds = connectionKeyIds.filter(
                (id: any) => !cachedKeys.some((key: any) => key.id === id),
              );

              let resolvedKeys = [...cachedKeys];
              if (missingKeyIds.length > 0) {
                try {
                  const newKeys = await getAllConnectionKeys(missingKeyIds);
                  if (newKeys?.length > 0) {
                    resolvedKeys = [...cachedKeys, ...newKeys];
                    // Persist newly fetched keys so subsequent edits are instant.
                    setFetchedConnectionKeys(resolvedKeys);
                  }
                } catch (error) {
                  console.error(
                    "Error fetching connection keys for edit mode:",
                    error,
                  );
                  showToast("Failed to load connection keys", "error");
                  // Fallback: use whatever is already cached.
                }
              }

              connectionKey = sources
                .map((src: any) =>
                  resolvedKeys.find(
                    (key: any) => key?.id === src?.connection_key_id,
                  ),
                )
                .filter(Boolean);
            } else {
              connectionKey = [];
            }

            // For non-Mixed queries simplify arrays to single items.
            if (!isMixed) {
              linkedService = linkedService[0];
              connectionKey = connectionKey[0];
            }

            const updatedFormData: any = {
              ...formData,
              source:
                formData.source[0].type === "Mixed"
                  ? formData.source.map((item: any) => ({ ...item }))
                  : formData.source[0],
              linked_service: linkedService,
              connection_key: connectionKey,
            };

            setEditFormData(updatedFormData);
            setQueryBuilderTempValue(formData?.query);
            setTemporaryQueryValue(formData?.query);
            setIsEditQuery(true);
            setQueryType(params?.row?.source[0]?.type);

            if (isFromExecution) {
              setGlobalVariables(
                viewInlineVariables?.used_generic_research_query_variables,
              );
            }
          } catch (error) {
            console.error("Error in handleEditQuery:", error);
            showToast("Failed to edit query", "error");
          } finally {
            isEditQueryInProgressRef.current = false;
            setLoading("editQuery")(false);
          }
        };

        return (
          <>
            {canEditGenericResearchQuery && (
              <IconButton onClick={handleEditQuery}>
                <IconEditBase />
              </IconButton>
            )}
            {!isFromExecution && canEditGenericResearchQuery && (
              <IconButton
                onClick={() => {
                  setSelectedQueryToDelete(params.row);
                  setOpenValidationExecutionConfirmation(true);
                }}
              >
                <IconDeleteBlueSvg />
              </IconButton>
            )}
          </>
        );
      },
    });

    return baseColumns;
  };

  const researchColumns: GridColDef[] = getResearchColumns();

  // Index of the row currently being edited (-1 when not editing)
  const editRowIndex = useMemo(() => {
    if (!isEditQuery || !editFormData) return -1;
    return (
      researchQueryForm?.research_queries?.findIndex(
        (q: any) => q.id === (editFormData as any)?.id,
      ) ?? -1
    );
  }, [isEditQuery, editFormData, researchQueryForm?.research_queries]);

  // Rows that sit ABOVE the edited row (shown in a separate DataTable above Configuration)
  const rowsAbove =
    editRowIndex > 0
      ? (researchQueryForm?.research_queries?.slice(0, editRowIndex) ?? [])
      : [];

  // The edited row + every row below it (shown in a header-less DataTable after Configuration)
  const rowsAtAndBelow =
    editRowIndex >= 0
      ? (researchQueryForm?.research_queries?.slice(editRowIndex) ?? [])
      : [];

  const handleSelectQueryType = (value: any) => {
    if (value) {
      setQueryType(value);
      if (value === "Resource") {
        setQueryBuilderTempValue(`SELECT * FROM <>`);
      } else if (value === "External") {
        setQueryBuilderTempValue(`SELECT * FROM <>`);
      } else if (value === "Mixed") {
        setQueryBuilderTempValue("");
      }
    } else {
      setQueryType("");
    }
  };
  const impactSumError = getImpactSumError(errors);

  // Entry point for the Mixed Query V2 module (mirrors the legacy "Configure"
  // button). Opening the editor is gated on isEditQuery, so a brand-new Mixed
  // query needs a blank editFormData seeded with the next id before the dialog
  // can render and save (handleSaveMixedQuery requires editFormData.id).
  const handleConfigureMixedQuery = () => {
    if (!editFormData) {
      const existing = researchQueryForm?.research_queries ?? [];
      setEditFormData({
        id: existing.length > 0 ? existing.length + 1 : 1,
        name: "",
        query: "",
        source: [],
      } as any);
    }
    setIsEditQuery(true);
  };

  // Update the Mixed query name and clear its validation error live once a
  // non-empty value is typed (parity with the sub-query field-level clearing).
  const handleMixedQueryNameChange = (value: string) => {
    setMixedQueryName(value);
    if (value?.trim()) {
      setMixedErrors((prev) => {
        if (!prev.name) return prev;
        const { name, ...rest } = prev;
        return rest;
      });
    }
  };

  // Update the Mixed Main Query and clear its validation error (index -1) live
  // once non-empty content is entered.
  const handleMixedMainQueryChange = (value: string) => {
    setMixedMainQuery(value);
    if (value?.trim()) {
      setMixedErrors((prev) => {
        if (!prev[-1]) return prev;
        const next = { ...prev };
        delete next[-1];
        return next;
      });
    }
  };

  const handleChangeFormData = (event: any) => {
    const { name, value } = event.target;
    setResearchQueryForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormHasChanges(true);
    name !== "description" && validateField(name, value);
  };

  // Severity mapping handlers
  const severityEntries = Object.entries(severityMappings);

  const queryNameOptions = useMemo(() => {
    return (researchQueryForm?.research_queries || [])
      .map((q: any) => q.name)
      .filter(Boolean);
  }, [researchQueryForm?.research_queries]);

  const handleAddSeverityMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setSeverityMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
    setFormHasChanges(true);
  };

  const handleRemoveSeverityMapping = (queryName: string) => {
    setSeverityMappings((prev) => {
      const next = { ...prev };
      delete next[queryName];
      return next;
    });
    setFormHasChanges(true);
  };

  const handleUpdateQueryName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setSeverityMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
    setFormHasChanges(true);
  };

  const handleUpdateMappingSeverity = (queryName: string, severity: string) => {
    setSeverityMappings((prev) => ({ ...prev, [queryName]: severity }));
    setFormHasChanges(true);
  };
  const handleRuleChange = (_: any, selectedOption: any | null) => {
    setResearchQueryForm((prev) => ({
      ...prev,
      rule_id: selectedOption?.id || null,
    }));
    setFormHasChanges(true);
    validateField("rule_id", selectedOption?.id || null);
  };
  const validateField = async (name: any, value: any) => {
    const nextFormData = { ...researchQueryForm, [name]: value };
    const scoringFields = [
      "impact_of_record_level",
      "impact_of_issue_level",
      "factor_low",
      "factor_medium",
      "factor_high",
    ];

    if (scoringFields.includes(name)) {
      const scoringErrors = await collectSchemaErrors(
        genericResearchQueryScoringSchema,
        nextFormData,
      );
      setErrors((previous: any) => {
        const next = { ...previous };
        scoringFields.forEach((field) => delete next[field]);
        return { ...next, ...scoringErrors };
      });
      return;
    }

    try {
      await GenericResourceFormSchema.validateAt(name, nextFormData);
      setErrors((previous: any) => ({ ...previous, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((previous: any) => ({
        ...previous,
        [name]: validationError.message,
      }));
    }
  };
  const [
    openValidationExecutionConfirmation,
    setOpenValidationExecutionConfirmation,
  ] = useState(false);
  const [selectedQueryToDelete, setSelectedQueryToDelete] = useState<any>(null);

  const handleValidationExecutionDelete = () => {
    setOpenValidationExecutionConfirmation(false);
    if (!selectedQueryToDelete) return;

    const updatedQueryData = researchQueryForm?.research_queries.filter(
      (query) => query.id !== selectedQueryToDelete.id,
    );
    setResearchQueryForm((prev: any) => ({
      ...prev,
      research_queries: updatedQueryData,
    }));

    if (isFromExecution) {
      // Create a new object with the updated research queries
      const updatedResearchQueryData = {
        ...researchQueryDetailData,
        research_queries: updatedQueryData,
      };

      const { generic_research_query_tags, ...rest } = updatedResearchQueryData;
      const finalPayload = {
        generic_research_query: { ...rest },
        generic_research_query_tags,
      };

      // Use the handler to update the parent's state
      handleSetResearchQueryDetailData(finalPayload);
      showToast(
        `${
          selectedQueryToDelete?.name ?? "Research Query"
        } deleted successfully!`,
        "success",
      );
      setSelectedQueryToDelete(null);
      return;
    }

    const payload = {
      ...researchQueryForm,
      research_queries: updatedQueryData,
      variables: globalVariables,
    };

    const { generic_research_query_tags, ...rest } = payload;
    const finalPayload = {
      generic_research_query: { ...rest },
      generic_research_query_tags,
    };
    updateGenericResearchQuery({
      currentResearchQueryId: researchQueryDetailData.id,
      payload: finalPayload,
    })
      .then((response: any) => {
        if (response) {
          showToast(
            `${
              selectedQueryToDelete?.name ?? "Research Query"
            } deleted successfully!`,
            "success",
          );
        }
      })
      .catch((err: Error) => {
        console.error("Error deleting research query:", err);
        showToast(`Cannot delete Research Query`, "error");
      });

    setSelectedQueryToDelete(null); // clear state
  };

  const handleCancelClick = () => {
    if (researchQueryDetailData) {
      const updatedResearchQueryData =
        researchQueryDetailData?.research_queries?.map(
          (researchQuery: any, index: number) => ({
            ...researchQuery,
            source: researchQuery?.source,
            id: index + 1,
            updatedQuery: researchQuery?.query,
            query: replaceQueryFromDomainMapped(
              researchQuery?.query,
              columns?.missingColumns ?? [],
            ),
          }),
        );

      setResearchQueryForm((prev: any) => ({
        ...prev,
        code: researchQueryDetailData?.code,
        name: researchQueryDetailData?.name,
        description: researchQueryDetailData?.description || "",
        research_queries: updatedResearchQueryData ?? [],
      }));

      setGlobalVariables(researchQueryDetailData?.variables ?? {});
    }

    setSeverityMappings(initialResearchQueryForm?.severity_mappings ?? {});
    setFormHasChanges(false);
    setIsEditQuery(false);
    setQueryType("");
    setEditFormData(undefined);
    setIsAddQueryBtnEnabled(true);
  };

  const handleCloseCloneResearchQuery = () => {
    setCloneResearchQueryDialog(false);
    setCloneResearchQueryData({
      name: "",
      code: "",
      description: "",
      generic_research_query_tags: [],
    });
  };

  const onSaveCloneResearchQuery = () => {
    const reqBody = {
      generic_research_query_id: Number(currentResearchQueryId),
      generic_research_query_name: cloneResearchQueryData.name,
      generic_research_query_code: cloneResearchQueryData.code,
      generic_research_query_desc: cloneResearchQueryData.description || "",
      generic_research_query_tags:
        cloneResearchQueryData.generic_research_query_tags || [],
    };
    cloneGenericResearchQuery(reqBody)
      .then((response) => {
        showToast("Research Query cloned successfully!", "success");
        navigate(`/research-query/view/${response?.id}`);
      })
      .catch((error) => {
        showToast(`Cannot create clone Research Query`, "error");
      });
    handleCloseCloneResearchQuery();
  };

  return (
    <>
      {Object.values(loadingStates).some(Boolean) && <Loader isLoading />}
      {!isFromExecution && (
        <Box className="rule-domain-chart-box">
          <h3>Details</h3>
          <Grid container columnSpacing={1.5} rowSpacing={2.5}>
            <Grid item xl={3} lg={3} md={3} sm>
              {isViewOnly ? (
                <>
                  <label className="label-text">Name</label>
                  <TextField
                    type="text"
                    value={researchQueryForm?.name || ""}
                    className="form-control read-only"
                    disabled
                  />
                </>
              ) : (
                <TextField
                  label={
                    <span>
                      Name
                      <span className="required-asterisk">*</span>
                    </span>
                  }
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={`form-control-autocomplete ${
                    errors?.name ? "has-error" : ""
                  }`}
                  name="name"
                  value={researchQueryForm?.name}
                  onChange={handleChangeFormData}
                  error={!!errors?.name}
                  helperText={errors?.name}
                />
              )}
            </Grid>
            <Grid item xl={3} lg={3} md={3} sm>
              {isViewOnly ? (
                <>
                  <label className="label-text">Code</label>
                  <TextField
                    type="text"
                    value={researchQueryForm?.code || ""}
                    className="form-control read-only"
                    disabled
                  />
                </>
              ) : (
                <TextField
                  label={
                    <span>
                      Code
                      <span className="required-asterisk">*</span>
                    </span>
                  }
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={`form-control-autocomplete ${
                    errors?.code ? "has-error" : ""
                  }`}
                  name="code"
                  value={researchQueryForm?.code}
                  onChange={handleChangeFormData}
                  error={!!errors?.code}
                  helperText={errors?.code}
                  disabled={!!currentRQId}
                />
              )}
            </Grid>
            <Grid item xl={3} lg={3} md={3} sm>
              {isViewOnly ? (
                <>
                  <label className="label-text">Description</label>
                  <TextField
                    type="text"
                    value={researchQueryForm?.description || ""}
                    className="form-control read-only"
                    disabled
                  />
                </>
              ) : (
                <TextField
                  label={<span>Description</span>}
                  type="text"
                  fullWidth
                  variant="outlined"
                  className={`form-control-autocomplete`}
                  name="description"
                  value={researchQueryForm?.description}
                  onChange={handleChangeFormData}
                  error={!!errors?.description}
                  helperText={errors?.description}
                />
              )}
            </Grid>
            <Grid item xl={3} lg={3} md={3} sm>
              {isViewOnly ? (
                <>
                  <label className="label-text">Tag(s)</label>
                  <ViewAuthTags
                    entityTagsDetails={
                      researchQueryDetailData?.generic_research_query_tags_details ||
                      []
                    }
                    entityName="Generic Research Query"
                    showToast={showToast}
                  />
                </>
              ) : (
                <EditAuthTags
                  selectedTagIds={
                    researchQueryForm?.generic_research_query_tags || []
                  }
                  onTagsChange={(selectedIds) => {
                    setResearchQueryForm((prev: any) => ({
                      ...prev,
                      generic_research_query_tags: selectedIds,
                    }));
                    setFormHasChanges(true);
                  }}
                  entityName="Generic Research Query"
                  showToast={showToast}
                  errors={errors?.generic_research_query_tags}
                  validateField={validateField}
                  fieldName="generic_research_query_tags"
                  label="Tag(s)"
                  required={true}
                  useActiveTagsOnly={true}
                  setIsLoading={setLoading("allRules")}
                />
              )}
            </Grid>
            <Grid item xl={3} lg={3} md={3} sm>
              {isViewOnly ? (
                <>
                  <label className="label-text">Rule</label>
                  <TextField
                    type="text"
                    value={
                      allRulesData?.find(
                        (option: any) =>
                          option.id === researchQueryForm?.rule_id,
                      )?.name || "—"
                    }
                    className="form-control read-only"
                    disabled
                  />
                </>
              ) : (
                <Autocomplete
                  fullWidth
                  options={Array.isArray(allRulesData) ? allRulesData : []}
                  getOptionLabel={(option: any) => option.name}
                  value={
                    allRulesData?.find(
                      (option: any) => option.id === researchQueryForm?.rule_id,
                    ) || null
                  }
                  renderInput={(params) => (
                    <TextField
                      label={<span>Select Rule</span>}
                      name="rule_id"
                      style={{ color: "#000000" }}
                      {...params}
                      placeholder="Select..."
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  renderOption={(params: any, item: any) => (
                    <li
                      {...params}
                      key={item.key}
                      style={{
                        paddingTop: "2px",
                        paddingBottom: "2px",
                      }}
                    >
                      <ListItemText>{item.name}</ListItemText>
                    </li>
                  )}
                  loadingText="Loading..."
                  onChange={handleRuleChange}
                  className={`form-control-autocomplete`}
                />
              )}
            </Grid>

            {/* {isViewOnly && canEditGenericResearchQuery && (
              <Grid
                item
                xs
                sx={{
                  justifyContent: "flex-end",
                  display: "flex",
                  alignItems: "center",
                  columnGap: "16px",
                }}
              >
                <button
                  className="btn-nostyle icon-btn-edit"
                  onClick={() =>
                    navigate(`/research-query/edit/${currentRQId}`)
                  }
                >
                  <IconBtnEditBase ariaLabel="Edit Research Query" />
                </button>
              </Grid>
            )} */}
          </Grid>
        </Box>
      )}

      {!isFromExecution && (
        <Grid container sx={{ pt: "11px" }}>
          <Grid
            item
            xs
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              columnGap: 1.5,
            }}
          >
            <>
              {researchQueryForm?.research_queries?.length > 0 ? (
                <>
                  {!isViewOnly && (formHasChanges || isEditQuery) ? (
                    <>
                      <Button
                        variant="contained"
                        color="secondary"
                        className="btn-orange btn-dark"
                        onClick={handleCancelClick}
                      >
                        Cancel
                      </Button>

                      {canEditGenericResearchQuery && (
                        <Button
                          variant="contained"
                          color="secondary"
                          className="btn-orange"
                          onClick={() => handleSaveResearchQuery()}
                          disabled={
                            !hasChanges ||
                            !!errors.impact_of_record_level ||
                            !!errors.impact_of_issue_level ||
                            !!errors.factor_low ||
                            !!errors.factor_medium ||
                            !!errors.factor_high
                          }
                        >
                          <SaveOutlinedIcon /> Save
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      {currentResearchQueryId && canAddGenericResearchQuery && (
                        <Tooltip
                          title="Clone Research Query"
                          placement="top"
                          arrow
                        >
                          <Button
                            variant="contained"
                            color="secondary"
                            className="btn-orange btn-nostyle"
                            onClick={() => {
                              // Auto-populate clone dialog with current research query data
                              setCloneResearchQueryData({
                                name: `Copy_${
                                  researchQueryDetailData?.name || ""
                                }`,
                                code: `Copy_${
                                  researchQueryDetailData?.code || ""
                                }`,
                                description:
                                  researchQueryDetailData?.description || "",
                                generic_research_query_tags:
                                  researchQueryDetailData?.generic_research_query_tags_details?.map(
                                    (tag: any) => tag.auth_tag_id,
                                  ) || [],
                              });
                              setCloneResearchQueryDialog(true);
                            }}
                          >
                            clone
                          </Button>
                        </Tooltip>
                      )}
                      {currentResearchQueryId &&
                        canExecuteGenericResearchQuery && (
                          <Tooltip title="Execute Query" placement="top" arrow>
                            <Button
                              variant="contained"
                              color="secondary"
                              className="btn-orange"
                              onClick={handleExecuteQuery}
                            >
                              Execute
                            </Button>
                          </Tooltip>
                        )}
                      {isViewOnly &&
                        currentResearchQueryId &&
                        canEditGenericResearchQuery && (
                          <Tooltip
                            title="Edit Research Query"
                            placement="top"
                            arrow
                          >
                            <button
                              className="btn-nostyle icon-btn-edit"
                              onClick={() =>
                                navigate(`/research-query/edit/${currentRQId}`)
                              }
                            >
                              <IconBtnEditBase ariaLabel="Edit Research Query" />
                            </button>
                          </Tooltip>
                        )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {!isViewOnly && canEditGenericResearchQuery && (
                    <Button
                      variant="contained"
                      color="secondary"
                      className="btn-orange"
                      onClick={() => handleSaveResearchQuery()}
                      disabled={
                        !(isAddQueryBtnEnabled && queryType === "") ||
                        !!errors.impact_of_record_level ||
                        !!errors.impact_of_issue_level ||
                        !!errors.factor_low ||
                        !!errors.factor_medium ||
                        !!errors.factor_high
                      }
                    >
                      <SaveOutlinedIcon /> Save
                    </Button>
                  )}
                </>
              )}

              {!isViewOnly &&
                (canEditGenericResearchQuery || canAddGenericResearchQuery) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    className="btn-orange"
                    onClick={() => setIsAddQueryBtnEnabled(false)}
                    disabled={!(isAddQueryBtnEnabled && queryType === "")}
                  >
                    + &nbsp;Add Query
                  </Button>
                )}
              {!isViewOnly && (
                <ViewJsonSplitButton
                  onViewJson={() => setIsPayloadDialogOpen(true)}
                  onCopyJson={handleCopyPayload}
                />
              )}
            </>
          </Grid>
        </Grid>
      )}

      {!isFromExecution && (
        <Tabs
          sx={{ mt: "8px" }}
          value={activeTab}
          onChange={handleChangeTab}
          className="mui-tabs alternative-1 min-height-0"
          variant="scrollable"
        >
          <Tab label="Queries" value="queries" />
          <Tab label="Scoring Config" value="scoringConfig" />
        </Tabs>
      )}

      {!isFromExecution &&
        (
          {
            queries: null,
            scoringConfig: (
              <Box
                sx={{ p: 2, mt: 0, mb: 0 }}
                className="accordion-panel text-box-card compact-text-box-card no-radius bottom-radius"
              >
                <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Validation Scoring Configuration
                </Typography>
                <Grid
                  container
                  rowSpacing={2.5}
                  columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
                >
                  {/* Scoring Configuration — Impact fields: Slider */}
                  {(
                    [
                      {
                        name: "impact_of_record_level",
                        label: "Impact of Record Level",
                      },
                      {
                        name: "impact_of_issue_level",
                        label: "Impact of Issue Level",
                      },
                    ] as { name: string; label: string }[]
                  ).map(({ name, label }) => {
                    const numVal =
                      parseFloat(
                        String(
                          researchQueryForm?.[
                            name as keyof typeof researchQueryForm
                          ] ??
                            validationScoringConfigDefaults[
                              name as keyof typeof validationScoringConfigDefaults
                            ],
                        ),
                      ) || 0;
                    return (
                      <Grid item xs={12} sm={6} md={6} key={name}>
                        <label className="label-text">
                          {label}: {numVal.toFixed(2)}
                        </label>
                        <Slider
                          name={name}
                          value={numVal}
                          onChange={(_, val) => {
                            const v = Array.isArray(val) ? val[0] : val;
                            handleChangeFormData({
                              target: { name, value: v },
                            });
                          }}
                          disabled={isViewOnly || !canEditGenericResearchQuery}
                          min={0}
                          max={1}
                          step={0.05}
                          marks={[
                            { value: 0, label: "0" },
                            { value: 0.5, label: "0.5" },
                            { value: 1, label: "1" },
                          ]}
                          valueLabelDisplay="auto"
                          sx={{
                            color: SLIDER_ACCENT,
                            "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
                            "& .MuiSlider-track": {
                              color: SLIDER_ACCENT,
                              border: "none",
                            },
                            "& .MuiSlider-rail": {
                              color: SLIDER_ACCENT,
                              opacity: 0.3,
                            },
                            "& .MuiSlider-valueLabel": {
                              backgroundColor: SLIDER_ACCENT,
                            },
                            "& .MuiSlider-markActive": {
                              backgroundColor: SLIDER_ACCENT,
                            },
                          }}
                        />
                        {errors?.[name] && errors[name] !== impactSumError && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {errors[name]}
                          </Typography>
                        )}
                      </Grid>
                    );
                  })}

                  {impactSumError && !isViewOnly && (
                    <Grid item xs={12}>
                      <Typography color="error" variant="body2">
                        {impactSumError}
                      </Typography>
                    </Grid>
                  )}

                  {/* Scoring Configuration — Factor fields: TextField only */}
                  {(
                    [
                      { name: "factor_low", label: "Factor (Low)" },
                      { name: "factor_medium", label: "Factor (Medium)" },
                      { name: "factor_high", label: "Factor (High)" },
                    ] as { name: string; label: string }[]
                  ).map(({ name, label }) => {
                    const val =
                      researchQueryForm?.[
                        name as keyof typeof researchQueryForm
                      ] ??
                      validationScoringConfigDefaults[
                        name as keyof typeof validationScoringConfigDefaults
                      ];
                    return (
                      <Grid item xs={12} sm={4} md={4} key={name}>
                        <label className="label-text">{label}</label>
                        {isViewOnly ? (
                          <TextField
                            type="text"
                            value={val}
                            className="form-control read-only"
                            disabled
                          />
                        ) : (
                          <TextField
                            type="number"
                            name={name}
                            fullWidth
                            className={`form-control ${
                              errors?.[name] ? "has-error" : ""
                            }`}
                            value={val}
                            onChange={(e) =>
                              handleChangeFormData({
                                target: {
                                  name,
                                  value: parseFloat(e.target.value) || 0,
                                },
                              })
                            }
                            InputProps={{
                              readOnly: !canEditGenericResearchQuery,
                            }}
                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                            error={!!errors?.[name]}
                            helperText={errors?.[name] || " "}
                          />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Severity Mappings Section */}
                <Typography
                  variant="body2"
                  sx={{ mb: 1, mt: 3, fontWeight: 600 }}
                >
                  Severity Mappings (query_name to severity)
                </Typography>
                <Box
                  sx={{
                    border: "1px solid var(--table-border)",
                    borderRadius: "4px",
                    mb: 2,
                    overflow: "hidden",
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Query Name</strong>
                          </TableCell>
                          <TableCell sx={{ width: 160 }}>
                            <strong>Severity</strong>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: 60 }}
                          ></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {severityEntries.map(([queryName, severity]) => {
                          const usedNames = Object.keys(
                            severityMappings,
                          ).filter((n) => n !== queryName);
                          const availableOptions = queryNameOptions.filter(
                            (opt: string) => !usedNames.includes(opt),
                          );
                          return (
                            <TableRow key={queryName}>
                              <TableCell>
                                <Autocomplete
                                  value={
                                    queryName.startsWith("__new_")
                                      ? null
                                      : queryName || null
                                  }
                                  options={availableOptions}
                                  disabled={
                                    isViewOnly || !canEditGenericResearchQuery
                                  }
                                  onChange={(_e, newValue) =>
                                    handleUpdateQueryName(
                                      queryName,
                                      newValue || "",
                                    )
                                  }
                                  className="form-control-autocomplete dropdown-padding-1"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      className="form-control"
                                      placeholder="Select query name"
                                    />
                                  )}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ width: 160 }}>
                                <Select
                                  fullWidth
                                  value={severity}
                                  disabled={
                                    isViewOnly || !canEditGenericResearchQuery
                                  }
                                  onChange={(e) =>
                                    handleUpdateMappingSeverity(
                                      queryName,
                                      e.target.value,
                                    )
                                  }
                                  size="small"
                                >
                                  {scoringSeverityOptions.map((opt: string) => (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell align="center">
                                {!isViewOnly && canEditGenericResearchQuery && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveSeverityMapping(queryName)
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {!isViewOnly && canEditGenericResearchQuery && (
                      <Box
                        sx={{
                          p: 1,
                          borderTop: "1px solid var(--table-border)",
                        }}
                      >
                        <Button
                          size="small"
                          className="btn-orange btn-sm"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddSeverityMapping}
                        >
                          Add Mapping
                        </Button>
                      </Box>
                    )}
                  </TableContainer>
                </Box>
              </Box>
            ),
          } as Record<string, React.ReactNode>
        )[activeTab]}

      {(activeTab === "queries" || isFromExecution) && (
        <Box className="pagination-not-required" sx={{ marginTop: "-6px" }}>
          {!isFromExecution && isAddQueryBtnEnabled && !isEditQuery && (
            <GenericResearchQueriesEmptyState
              queries={researchQueryForm?.research_queries}
            />
          )}

          {/* When editing (non-Mixed): rows ABOVE the edited row — full header visible.
            For Mixed queries this block is intentionally suppressed so the
            Configuration box always renders at the top of the list. */}
          {isEditQuery &&
            queryType !== "Mixed" &&
            rowsAbove.length > 0 &&
            (!isFromExecution || (isFromExecution && !isEditQuery)) && (
              <DataTable
                dataRows={rowsAbove}
                dataColumns={researchColumns}
                dataListTitle=""
                className="dataTable hide-progress-icon no-buttons"
                paginationMode="client"
                isExportButtonDisabled={true}
                isPaginationRequired={false}
                {...(isFromExecution
                  ? { minHeight: "inherit", topMargin: 0 }
                  : { minHeight: "inherit", topMargin: "6px" })}
              />
            )}

          {/* Configuration form: above all rows when adding new; inline above edited row when editing */}
          {!isViewOnly && (!isAddQueryBtnEnabled || isEditQuery) && (
            <Box className="rule-domain-chart-box" sx={{ marginTop: "6px" }}>
              <h3>Configuration</h3>
              <Grid container columnSpacing={1.5} rowSpacing={2.5}>
                {!isAddQueryBtnEnabled && (
                  <Grid item xl={3} lg={3} md={3}>
                    <Autocomplete
                      fullWidth
                      options={GenericQueriesType ?? []}
                      getOptionLabel={(option: any) =>
                        option ? `${option.replace("_", " ")} Queries` : ""
                      }
                      value={
                        GenericQueriesType?.find(
                          (option) => option === queryType,
                        ) || null
                      }
                      renderInput={(params) => (
                        <TextField
                          label={
                            <span>
                              Select Query Type
                              <span className="required-asterisk">*</span>
                            </span>
                          }
                          name="queryType"
                          style={{ color: "#000000" }}
                          {...params}
                          placeholder="Select..."
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                      renderOption={(params: any, item: any) => (
                        <li
                          {...params}
                          key={item.key}
                          style={{
                            paddingTop: "2px",
                            paddingBottom: "2px",
                          }}
                        >
                          <ListItemText>
                            <span
                              style={{ textTransform: "capitalize" }}
                            >{`${item.replace("_", " ")} Queries`}</span>
                          </ListItemText>
                        </li>
                      )}
                      loadingText="Loading..."
                      onChange={(_, value) => {
                        handleSelectQueryType(value);
                        setShowAggregatedColumns(undefined);
                      }}
                      className={`form-control-autocomplete`}
                    />
                  </Grid>
                )}

                {/* Entry point for the Mixed Query V2 editor. Mirrors the legacy
                    "Configure" button: visible only for the Mixed type and
                    positioned adjacent to the Query Type selector. */}
                {queryType === "Mixed" && !isEditQuery && (
                  <Grid item>
                    <label className="label-text line-height16">&nbsp;</label>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "8px",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        className="btn-orange"
                        onClick={handleConfigureMixedQuery}
                        disabled={isFromExecution}
                      >
                        + &nbsp;Configure
                      </Button>
                      <Tooltip
                        title="Click on configure to add/modify sub queries"
                        placement="top"
                        arrow
                      >
                        <span>
                          <InfoIcon sx={{ display: "block" }} />
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                {queryType === "Resource" && (
                  <GenericResourceQueryTab
                    researchQueryData={researchQueryForm}
                    setResearchQueryData={setResearchQueryForm}
                    handleTransition={handleTransition}
                    resourcesData={allResourcesData}
                    renderFrom="rule"
                    columns={columns}
                    setColumns={setColumns}
                    isEditQuery={isEditQuery}
                    setIsEditQuery={setIsEditQuery}
                    editFormData={editFormData}
                    setEditFormData={setEditFormData}
                    setQueryType={setQueryType}
                    handleSaveResearchQuery={(researchQuery) =>
                      handleSaveResearchQuery(researchQuery)
                    }
                    setEditorValue={setQueryBuilderTempValue}
                    setIsAddQueryBtnEnabled={setIsAddQueryBtnEnabled}
                    isFromExecution={isFromExecution}
                    setErrors={setErrors}
                  />
                )}
                {queryType === "External" && (
                  <GenericExternalQueryTab
                    researchQueryData={researchQueryForm}
                    setResearchQueryData={setResearchQueryForm}
                    handleTransition={handleTransition}
                    columns={columns}
                    setColumns={setColumns}
                    isEditQuery={isEditQuery}
                    setIsEditQuery={setIsEditQuery}
                    editFormData={editFormData}
                    setEditFormData={setEditFormData}
                    setQueryType={setQueryType}
                    handleSaveResearchQuery={(researchQuery) =>
                      handleSaveResearchQuery(researchQuery)
                    }
                    setIsAddQueryBtnEnabled={setIsAddQueryBtnEnabled}
                    isFromExecution={isFromExecution}
                    setErrors={setErrors}
                  />
                )}

                {queryType === "Mixed" && isEditQuery && (
                  <MixedQueryEditorDialog
                    open={isEditQuery}
                    onClose={handleCloseMixedDialog}
                    onSave={handleSaveMixedQuery}
                    queryName={mixedQueryName}
                    setQueryName={handleMixedQueryNameChange}
                    queryError={mixedErrors}
                    subQueries={mixedSubQueries}
                    activeIndex={mixedActiveIndex}
                    setActiveIndex={setMixedActiveIndex}
                    columns={columns}
                    setColumns={setColumns}
                    resourcesData={allResourcesData}
                    linkedServicesData={linkedServicesData}
                    mainQuery={mixedMainQuery}
                    setMainQuery={handleMixedMainQueryChange}
                    handlers={{
                      handleTypeChange: mixedHandleTypeChange,
                      handleFieldChange: mixedHandleFieldChange,
                      handleLinkedServiceChange: mixedHandleLinkedServiceChange,
                      handleQueryChange: mixedHandleQueryChange,
                      handleEditorFocus: mixedHandleEditorFocus,
                      handleEditorBlur: mixedHandleEditorBlur,
                      removeSubQuery: mixedRemoveSubQuery,
                      handleToggleActive: mixedHandleToggleActive,
                      getDependencyDisplayLabel: mixedGetDependencyDisplayLabel,
                      handleSetExternalColumns: mixedHandleSetExternalColumns,
                    }}
                    getSubQueryLabel={getSubQueryLabel}
                    onAddSource={
                      mixedSubQueries.length < MAX_SUBQUERIES
                        ? mixedAddSubQuery
                        : undefined
                    }
                    errors={mixedErrors}
                    isReadOnly={resolveMixedQueryReadOnly({
                      isViewOnly,
                      isFromExecution,
                      allowExecutionOverride,
                    })}
                  />
                )}
                {!isAddQueryBtnEnabled && queryType === "" && (
                  <Grid
                    item
                    xs
                    sx={{
                      display: "flex",
                      columnGap: "8px",
                      textAlign: "right",
                      justifyContent: "flex-end",
                      alignSelf: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      className="btn-orange btn-dark"
                      onClick={() => {
                        setIsAddQueryBtnEnabled(true);
                        setQueryType("");
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* When editing (non-Mixed): rows AT and BELOW the edited row — header hidden
            so it visually continues the rowsAbove table with the form in between. */}
          {isEditQuery &&
            queryType !== "Mixed" &&
            rowsAtAndBelow.length > 0 && (
              <Box
                sx={{
                  "& .MuiDataGrid-columnHeaders": { display: "none" },
                  "& .MuiDataGrid-virtualScroller": {
                    marginTop: "0 !important",
                  },
                }}
              >
                <DataTable
                  dataRows={rowsAtAndBelow}
                  dataColumns={researchColumns}
                  dataListTitle=""
                  className="dataTable hide-progress-icon no-buttons"
                  paginationMode="client"
                  isExportButtonDisabled={true}
                  isPaginationRequired={false}
                  {...(isFromExecution
                    ? { minHeight: "inherit", topMargin: 0 }
                    : { minHeight: "inherit", topMargin: "6px" })}
                />
              </Box>
            )}

          {/* When editing a Mixed query: show ALL rows below the Configuration box.
            rowsAbove is suppressed above so Configuration always appears at the top. */}
          {isEditQuery &&
            queryType === "Mixed" &&
            researchQueryForm?.research_queries?.length > 0 && (
              <DataTable
                dataRows={researchQueryForm.research_queries}
                dataColumns={researchColumns}
                dataListTitle=""
                className="dataTable hide-progress-icon no-buttons"
                paginationMode="client"
                singlePageMaxHeightDiff={307}
                isExportButtonDisabled={true}
                isPaginationRequired={false}
                {...(isFromExecution
                  ? { minHeight: "inherit", topMargin: 0 }
                  : { minHeight: "inherit", topMargin: "6px" })}
              />
            )}

          {/* When NOT editing: all rows as a single DataTable (original behaviour) */}
          {!isEditQuery &&
            researchQueryForm?.research_queries?.length > 0 &&
            (!isFromExecution || (isFromExecution && !isEditQuery)) && (
              <DataTable
                dataRows={researchQueryForm?.research_queries}
                dataColumns={researchColumns}
                dataListTitle=""
                className="dataTable hide-progress-icon no-buttons"
                paginationMode="client"
                singlePageMaxHeightDiff={307}
                isExportButtonDisabled={true}
                isPaginationRequired={false}
                {...(isFromExecution
                  ? { minHeight: "inherit", topMargin: 0 }
                  : { minHeight: "inherit", topMargin: "6px" })}
              />
            )}
        </Box>
      )}

      <ConfirmationDialog
        title={"Confirm removing Query"}
        dialogContent={"Are you sure you want to delete this query?"}
        handleCancel={() => setOpenValidationExecutionConfirmation(false)}
        openConfirmation={openValidationExecutionConfirmation}
        handleConfirm={handleValidationExecutionDelete}
        isShowClassMb0={false}
      />
      <CloneResearchQueryDialog
        cloneResearchQueryDialog={cloneResearchQueryDialog}
        handleCloseCloneResearchQuery={handleCloseCloneResearchQuery}
        cloneResearchQueryData={cloneResearchQueryData}
        setCloneResearchQueryData={setCloneResearchQueryData}
        onSaveCloneResearchQuery={onSaveCloneResearchQuery}
        setIsLoading={setLoading("cloneResearchQuery")}
      />
      <CommentBeforeUpdateDialog
        title={"Confirm updating Research Query"}
        dialogContent={
          <div>
            <p className="m-0 mb-2">
              Add a note before updating the Research Query
            </p>
            <textarea
              value={comment}
              className={`form-control-1 max-60`}
              onChange={(e) => handleCommentChange(e)}
            />
          </div>
        }
        openConfirmation={openCommentConfirmation}
        handleSaveComment={handleSaveComment}
        handleCancel={handleCancelComment}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Generic Research Query JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
}
